import React, { useEffect, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { fetchPerformas, saveSelectedPerformas } from "../actions/performaActions";
import { clearCustomsParams, setCustomsParams } from "../actions/customsParamsAction";
import ImportExcel from "../components/ImportExcel";
import "../style/CottageForm.css";
import "../style/importPrf.css";

const rowKey = (performa) => String(performa.prfVCodeInt ?? performa.prf_number);

const ImportPf = () => {
  const dispatch = useDispatch();
  const { loading, performas: fetchedPerformas, error, message } = useSelector((state) => state.performa);
  const { ssdsshGUID, urlVCodeInt } = useSelector((state) => state.customsDeclarations);
  const performas = Array.isArray(fetchedPerformas) ? fetchedPerformas : [];
  const [formData, setFormData] = useState({
    ssdsshGUID: ssdsshGUID || "",
    pageSize: "",
    urlVCodeInt: urlVCodeInt || "",
  });
  const [selectedPerformas, setSelectedPerformas] = useState([]);
  const [saving, setSaving] = useState(false);
  const [saveNotice, setSaveNotice] = useState(null);
  const selectAllRef = useRef(null);

  const allSelected = performas.length > 0 && selectedPerformas.length === performas.length;

  useEffect(() => {
    setFormData((current) => ({
      ...current,
      ssdsshGUID: ssdsshGUID || "",
      urlVCodeInt: urlVCodeInt || "",
    }));
  }, [ssdsshGUID, urlVCodeInt]);

  useEffect(() => {
    setSelectedPerformas([]);
    setSaveNotice(null);
  }, [fetchedPerformas]);

  useEffect(() => {
    if (selectAllRef.current) {
      selectAllRef.current.indeterminate = selectedPerformas.length > 0 && !allSelected;
    }
  }, [allSelected, selectedPerformas.length]);

  const handleSubmit = (event) => {
    event.preventDefault();
    setSaveNotice(null);
    const params = {
      ...formData,
      ssdsshGUID: formData.ssdsshGUID.trim(),
      urlVCodeInt: formData.urlVCodeInt.trim(),
    };
    dispatch(setCustomsParams(params.ssdsshGUID, params.urlVCodeInt));
    dispatch(fetchPerformas(params));
  };

  const handleClearParams = () => {
    if (!window.confirm("پارامترهای اتصال پاک شوند؟")) return;
    dispatch(clearCustomsParams());
    setFormData({ ssdsshGUID: "", pageSize: "", urlVCodeInt: "" });
    setSelectedPerformas([]);
    setSaveNotice({ type: "success", text: "پارامترها پاک شدند." });
  };

  const handleCheckboxChange = (checked, performa) => {
    setSaveNotice(null);
    setSelectedPerformas((current) => checked
      ? [...current.filter((item) => rowKey(item) !== rowKey(performa)), performa]
      : current.filter((item) => rowKey(item) !== rowKey(performa)));
  };

  const handleSendSelected = async () => {
    if (selectedPerformas.length === 0) {
      setSaveNotice({ type: "error", text: "ابتدا یک ثبت سفارش را انتخاب کنید." });
      return;
    }
    setSaving(true);
    setSaveNotice(null);
    try {
      const results = await Promise.allSettled(selectedPerformas.map((performa) =>
        dispatch(saveSelectedPerformas(performa, formData.ssdsshGUID.trim(), formData.urlVCodeInt.trim()))
      ));
      const saved = results.filter((result) => result.status === "fulfilled").length;
      const failed = results.length - saved;
      setSelectedPerformas(selectedPerformas.filter((_, index) => results[index].status === "rejected"));
      setSaveNotice({
        type: failed ? "error" : "success",
        text: failed
          ? `${saved} ثبت سفارش ذخیره شد و ${failed} مورد خطا داشت. موارد ناموفق انتخاب شده‌اند؛ دوباره تلاش کنید.`
          : `${saved} ثبت سفارش با موفقیت ذخیره شد.`,
      });
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="import-prf-container" dir="rtl">
      <header className="import-prf-header">
        <h1 className="title">دریافت ثبت سفارش‌ها از سامانه جامع تجارت</h1>
        <p>پارامترهای اتصال را وارد کنید، ثبت سفارش‌ها را دریافت کنید و موارد موردنظر را ذخیره کنید.</p>
      </header>

      <form className="import-form" onSubmit={handleSubmit}>
        <div className="form-group">
          <label htmlFor="ssdsshGUID">ssdsshGUID</label>
          <input id="ssdsshGUID" name="ssdsshGUID" type="text" dir="ltr" autoComplete="off"
            value={formData.ssdsshGUID} onChange={(event) => setFormData((current) => ({ ...current, ssdsshGUID: event.target.value }))}
            placeholder="603FF82E-5EAF-4DFA-BBC1-FA0030D686DB" required />
        </div>
        <div className="form-group">
          <label htmlFor="urlVCodeInt">urlVCodeInt</label>
          <input id="urlVCodeInt" name="urlVCodeInt" type="number" dir="ltr" autoComplete="off"
            value={formData.urlVCodeInt} onChange={(event) => setFormData((current) => ({ ...current, urlVCodeInt: event.target.value }))}
            placeholder="124013" required />
        </div>
        <div className="form-actions">
          <button type="submit" className="submit-button" disabled={loading}>
            {loading ? "در حال دریافت..." : "دریافت ثبت سفارش‌ها"}
          </button>
          <button type="button" className="clear-button" onClick={handleClearParams}
            disabled={!formData.ssdsshGUID && !formData.urlVCodeInt}>پاک کردن پارامترها</button>
        </div>
      </form>

      {loading && <p role="status" className="loading">در حال دریافت ثبت سفارش‌ها...</p>}
      {error && <p role="alert" className="error-message">{error}</p>}
      {message && <p role="status" className="success-message">{message}</p>}
      {saveNotice && <p role={saveNotice.type === "error" ? "alert" : "status"}
        className={saveNotice.type === "error" ? "error-message" : "success-message"}>{saveNotice.text}</p>}

      {ssdsshGUID && urlVCodeInt && (
        <section className="current-params">
          <h2>پارامترهای فعلی</h2>
          <p><strong>ssdsshGUID:</strong> <bdi>{ssdsshGUID}</bdi></p>
          <p><strong>urlVCodeInt:</strong> <bdi>{urlVCodeInt}</bdi></p>
          <ImportExcel />
        </section>
      )}

      {performas.length > 0 && (
        <section className="performas-section">
          <div className="performas-section-header">
            <h2>ثبت سفارش‌های دریافت‌شده</h2>
            <span>{selectedPerformas.length} مورد انتخاب شده از {performas.length} مورد</span>
          </div>
          <button type="button" onClick={handleSendSelected} className="save-selected-button"
            disabled={loading || saving || selectedPerformas.length === 0}>
            {saving ? "در حال ذخیره..." : "ذخیره موارد انتخاب‌شده"}
          </button>
          <div className="performas-table-container" role="region" aria-label="جدول ثبت سفارش‌ها" tabIndex="0">
            <table className="performas-table">
              <thead>
                <tr>
                  <th><input ref={selectAllRef} type="checkbox" checked={allSelected}
                    aria-label="انتخاب همه ثبت سفارش‌ها"
                    onChange={(event) => setSelectedPerformas(event.target.checked ? performas : [])} /></th>
                  <th>ردیف</th>
                  <th>شماره پیش فاکتور</th>
                  <th>شماره پرونده</th>
                  <th>شماره ثبت سفارش</th>
                  <th>تاریخ ثبت سفارش</th>
                  <th>ارزش کل</th>
                  <th>تاریخ اعتبار</th>
                </tr>
              </thead>
              <tbody>
                {performas.map((performa, index) => (
                  <tr key={rowKey(performa)}>
                    <td><input type="checkbox" aria-label={`انتخاب ثبت سفارش ${performa.prf_order_no || index + 1}`}
                      checked={selectedPerformas.some((item) => rowKey(item) === rowKey(performa))}
                      onChange={(event) => handleCheckboxChange(event.target.checked, performa)} /></td>
                    <td>{index + 1}</td>
                    <td>{performa.prf_number}</td>
                    <td>{performa.prfVCodeInt}</td>
                    <td>{performa.prf_order_no}</td>
                    <td>{performa.prf_date}</td>
                    <td>{performa.prf_total_price}</td>
                    <td>{performa.prf_expire_date}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </section>
      )}
    </div>
  );
};

export default ImportPf;
